import { create } from "zustand";
import { GeofenceSettings } from "@/types/IGeofence";
import { IOptions } from "@/types/IOptions";
import { UserScope } from "@/types/IUserScope";

interface OptionsState {
  departments: IOptions[];
  roles: IOptions[];
  permissions: IOptions[];
  responsibles: IOptions[];
  clients: IOptions[];
  setDepartments: (departments: IOptions[]) => void;
  setRoles: (roles: IOptions[]) => void;
  setPermissions: (permissions: IOptions[]) => void;
  setResponsibles: (responsibles: IOptions[]) => void;
  setClients: (clients: IOptions[]) => void;
}

export const useOptionsStore = create<OptionsState>((set) => ({
  departments: [],
  roles: [],
  permissions: [],
  responsibles: [],
  vacationStatuses: [],
  clients: [],
  setDepartments: (departments) => set({ departments }),
  setRoles: (roles) => set({ roles }),
  setPermissions: (permissions) => set({ permissions }),
  setResponsibles: (responsibles) => set({ responsibles }),
  setClients: (clients) => set({ clients }),
}));

interface IUser {
  id: number;
  name: string;
  email: string;
  image?: string | null;
  // superadmin | support | owner | admin | member
  type?: string;
  // Módulos liberados (relevante só para `member`).
  modules?: string[];
  // Escopo de visão do vínculo com a empresa ativa. Indefinido em contexto geral (sem empresa selecionada).
  scope?: UserScope;
}
interface ILoggedUser {
  loggedUser: IUser | null;
  setLoggedUser: (loggedUser: IUser | null) => void;
}

export const useLoggedUserStore = create<ILoggedUser>((set) => ({
  loggedUser: null,
  setLoggedUser: (loggedUser) => set({ loggedUser }),
}));

interface IMyFaceState {
  myEmployeeId: number | null;
  hasFacialEnrollment: boolean | null;
  setMyFacialStatus: (
    employeeId: number | null,
    hasFace: boolean | null
  ) => void;
  clearMyFacialStatus: () => void;
}

export const useMyFaceStore = create<IMyFaceState>((set) => ({
  myEmployeeId: null,
  hasFacialEnrollment: null,
  setMyFacialStatus: (myEmployeeId, hasFacialEnrollment) =>
    set({ myEmployeeId, hasFacialEnrollment }),
  clearMyFacialStatus: () =>
    set({ myEmployeeId: null, hasFacialEnrollment: null }),
}));

export interface ICompanyOption {
  id: number;
  name: string;
}

interface ICompanyState {
  currentCompanyId: number | null;
  companies: ICompanyOption[];
  setCurrentCompanyId: (id: number) => void;
  setCompanies: (companies: ICompanyOption[]) => void;
  clearCompanyContext: () => void;
}

export const useCompanyStore = create<ICompanyState>((set) => ({
  currentCompanyId: Number(localStorage.getItem("companyId")) || null,
  companies: [],
  setCurrentCompanyId: (id) => {
    localStorage.setItem("companyId", String(id));
    set({ currentCompanyId: id });
  },
  setCompanies: (companies) => set({ companies }),
  clearCompanyContext: () => {
    localStorage.removeItem("companyId");
    set({ currentCompanyId: null, companies: [] });
  },
}));

export interface IPipelineStage {
  key: string;
  label: string;
  probability: number;
  color: string;
}

export interface IModuleToggles {
  users: boolean;
  departments: boolean;
  leads: boolean;
  clients: boolean;
  contacts: boolean;
  projects: boolean;
  documents: boolean;
  subscriptions: boolean;
  finance: boolean;
  kanban: boolean;
  employees: boolean;
  timeclock: boolean;
}

export interface IReminderSettings {
  documents: {
    enabled: boolean;
    daysBefore: number[];
    trashRetentionDays: number;
  };
  tasks: { enabled: boolean; intervalDays: number };
  vacations: { enabled: boolean };
  followUps: { enabled: boolean; intervalDays: number };
  invoices: { enabled: boolean; intervalDays: number };
  subscriptions: { enabled: boolean; daysBefore: number[] };
  weeklyDigest: {
    enabled: boolean;
    weekday: number;
    time: string;
    includeDocuments: boolean;
    includeTasks: boolean;
    includeVacations: boolean;
    includeFollowUps: boolean;
    includeSubscriptions: boolean;
    includeInvoices: boolean;
  };
}

export interface IPublicTimeClockSettings {
  enabled: boolean;
  code: string | null;
}

export interface ICompanySettings {
  id?: number;
  displayName: string | null;
  currency: string;
  locale: string;
  timezone: string;
  pipelineStages: IPipelineStage[];
  modules: IModuleToggles;
  reminderSettings: IReminderSettings;
  publicTimeClock: IPublicTimeClockSettings;
  geofence: GeofenceSettings;
}

interface ISettingsState {
  settings: ICompanySettings | null;
  setSettings: (settings: ICompanySettings) => void;
}

export const useSettingsStore = create<ISettingsState>((set) => ({
  settings: null,
  setSettings: (settings) => set({ settings }),
}));
